import { Injectable, signal } from '@angular/core';
import posthog, { Properties } from 'posthog-js';
import { environment } from '../../environments/environment';
import { FeatureFlag, FeatureFlagRecord, FeatureFlagResult, RouteFeatureFlagPayload, UserProfile } from '../types';

@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  public isReady = signal<boolean>(false);
  public featureFlags = signal<FeatureFlagRecord>({});

  constructor() {
    posthog.init(environment.posthogApiKey, {
      api_host: 'https://app.posthog.com',
      disable_session_recording: true,
    });
    posthog.onFeatureFlags(() => {
      this.isReady.set(true);
      this.featureFlags.set({
        ...Object.values(FeatureFlag).reduce(
          (acc, flag) => ({
            ...acc,
            [flag]: this.isFeatureEnabled(flag, false),
          }),
          {},
        ),
      });
    });
  }

  capture(eventName: string, properties?: Properties) {
    return posthog.capture(eventName, properties);
  }

  identify(userId: string, email?: string, branchIds?: string[], systemRoles?: string[], locationRoles?: string[]) {
    posthog.identify(userId, {
      email,
      branchIds,
      systemRoles,
      locationRoles,
    });
    console.info('Starting session recording for user:', email);
    posthog.startSessionRecording(true);
  }

  isFeatureEnabled(flag: string, valueIfUndefined = false) {
    const posthogIsEnabled = posthog.isFeatureEnabled(flag);
    return posthogIsEnabled || valueIfUndefined;
  }

  getFeatureFlagPayloadIfEnabled<T>(flag: string, defaultValue: T = null): FeatureFlagResult<T> {
    if (this.isFeatureEnabled(flag)) {
      const result = posthog.getFeatureFlagPayload(flag) as T;
      return {
        enabled: true,
        payload: result ?? defaultValue,
      };
    }
    return { enabled: false };
  }

  isFeatureEnabledByEnum(flag: FeatureFlag, valueIfUndefined = false) {
    return this.featureFlags()[flag] || valueIfUndefined;
  }

  evaluateRouteFeatureFlag(featureFlagName: string, user: UserProfile): boolean {
    if (!featureFlagName || user.roles.system.includes('admin')) {
      return true;
    }

    const result = this.getFeatureFlagPayloadIfEnabled<RouteFeatureFlagPayload>(featureFlagName, null);

    if (!result.enabled) {
      return false;
    }

    if (!result.payload) {
      // No payload means the feature flag is managed by release conditions, so we allow access.
      return true;
    }

    const payload = result.payload;

    //Force each branchId to be a string
    const branchIds = payload.branchIds.map(String) || [];
    return Object.keys(user.roles.branch).some((str) => {
      switch (payload.mode) {
        case 'blacklist':
          return !branchIds.includes(str);
        case 'whitelist':
          return branchIds.includes(str);
        default:
          //disallow access for unknown modes
          return false;
      }
    });
  }
}
